// import useRouter from './useRouter';

import type { FunctionComponent, ReactNode } from 'react';
import { useMemo } from 'react';

import type PERMISSIONS from '@snowit/utilities/permissions';
import type { PathPattern } from 'wouter';
import type { NavRoute } from './types';

import useAuth from '@snowit/system/useAuth';

import AccessDenied from './AccessDenied';
import { useRouter } from './useRouter';

import { Route as WouterRoute } from 'wouter';

type RouteProps = {
  children?: ReactNode;
  path?: PathPattern;
  nest?: boolean;
  permissions?: PERMISSIONS[];
  component?: FunctionComponent;
};

const Route = (props: RouteProps) => {
  const { authType, permissions, hasEveryPermission, hasPermission } = useAuth();
  const { activeRoute } = useRouter();
  const routeType: NavRoute['type'] = activeRoute?.type ?? 0;
  const routePerms: NavRoute['permissions'] = activeRoute?.permissions ?? [];

  const reason: string | undefined = useMemo(() => {
    if (!props.path) return undefined;
    if (!activeRoute) return 'Unknown Route';

    if (authType === 'ADMIN') return undefined;
    if (authType === 'USER') {
      if (routeType === 2) return undefined;
      return `User cannot access ${activeRoute.path}`;
    }
    if (authType === 'PARTNER' && routeType < 1) {
      return `Partner cannot access ${activeRoute.path}`;
    }
    if (!routePerms.length) return undefined;

    let has = hasEveryPermission(...routePerms);
    if (activeRoute.logic === 'OR') {
      has = hasPermission(...routePerms);
    }
    if (!has) {
      return `Missing permissions ${routePerms.join(', ')}`;
    }
    return undefined;
  }, [authType, permissions, routeType, activeRoute, props.path]);

  if (reason) {
    return <AccessDenied reason={reason} />;
  }

  return (
    <WouterRoute path={props.path} nest={props.nest}>
      {props.component ? <props.component /> : props.children}
    </WouterRoute>
  );
};

export default Route;
